import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";
import { categoryMeta } from "@/mocks/anime";

const values = [
  { icon: "ri-database-2-line", title: "信息完整", desc: "每部作品保留类型、地区、年份、章节与制作信息，方便快速判断是否符合口味。" },
  { icon: "ri-filter-3-line", title: "检索高效", desc: "多维筛选与排序组合使用，从上千部作品里也能在几步之内锁定目标。" },
  { icon: "ri-heart-3-line", title: "体验克制", desc: "不做弹窗轰炸，不诱导点击，只把内容有序地摆在你面前。" },
];

const helpItems = [
  { step: "01", title: "找作品", desc: "在顶部搜索框输入作品名或类型，或从分类页按类型、地区、年份筛选。" },
  { step: "02", title: "看详情", desc: "进入详情页查看简介、章节列表与相关推荐，播放入口为演示占位。" },
  { step: "03", title: "做收藏", desc: "点击详情页的收藏按钮，记录会保存在当前浏览器的本地存储中。" },
  { step: "04", title: "分享链接", desc: "详情页的分享按钮会复制当前页面链接，可直接粘贴给朋友。" },
];

const milestones = [
  { year: "2023", title: "妖精动漫上线", desc: "最初只是一个整理国产动画更新表的个人页面。" },
  { year: "2024", title: "加入资讯频道", desc: "开始系统整理档期情报与行业观察，形成内容双线。" },
  { year: "2025", title: "分类与筛选重构", desc: "上线多维筛选、排序与联合搜索，让查找更直接。" },
  { year: "2026", title: "全端体验升级", desc: "完成移动端适配，用统一的视觉体系承载全部内容。" },
];

const team = [
  { name: "内容策划组", role: "作品资料 / 章节信息整理", icon: "ri-file-list-3-line" },
  { name: "资讯编辑组", role: "每日资讯与行业观察", icon: "ri-newspaper-line" },
  { name: "前端体验组", role: "页面结构、筛选与搜索交互", icon: "ri-layout-masonry-line" },
];

export default function AboutPage() {
  usePageMeta(
    "妖精动漫 - 关于我们 - 团队、使用说明与内容声明",
    "了解妖精动漫的定位与团队：我们专注国产、日韩动画内容整理与资讯跟进。页面同时提供站内使用说明、收藏与分享功能说明以及内容声明。"
  );

  return (
    <SiteLayout>
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 py-12 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold tracking-[0.22em] text-accent-400 mb-3">ABOUT FAIRY ANIME</p>
              <h1 className="text-3xl md:text-[40px] font-black leading-tight tracking-tight text-background-50">
                妖精动漫
                <span className="block text-[15px] md:text-base font-medium text-primary-200 mt-3 tracking-normal">
                  一个认真整理动画内容的门户站点
                </span>
              </h1>
              <p className="mt-5 text-[13px] md:text-[14px] leading-7 text-primary-200 max-w-[560px]">
                妖精动漫面向国产、日韩等各类动画的爱好者，提供作品资料查询、分类浏览、资讯阅读与推荐发现。
                我们相信内容站点最重要的能力是“让人快速找到想看的东西”，因此把筛选、搜索与信息结构放在第一位。
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/category/guoman"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-[13px] font-bold transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始浏览作品 <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-primary-700 text-background-100 text-[13px] font-medium hover:bg-primary-900 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-newspaper-line" /> 阅读资讯
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {[
                { value: "1,280+", label: "收录作品条目" },
                { value: "8", label: "主分类入口" },
                { value: "10", label: "细分类型标签" },
                { value: "每日", label: "资讯内容更新" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-primary-900 border border-primary-800 p-4 md:p-5">
                  <p className="text-2xl md:text-3xl font-black text-accent-400 tracking-tight">{stat.value}</p>
                  <p className="mt-1 text-[12px] text-primary-300">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="mb-6">
          <p className="text-[11px] font-bold tracking-[0.2em] text-accent-600 mb-2">WHAT WE CARE</p>
          <h2 className="text-xl md:text-2xl font-black text-foreground-950 tracking-tight">我们在意什么</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg bg-background-100 border border-background-200 p-5">
              <span className="w-10 h-10 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center">
                <i className={`${item.icon} text-lg`} />
              </span>
              <h3 className="mt-3.5 text-[15px] font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-[13px] leading-7 text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <p className="text-[11px] font-bold tracking-[0.2em] text-accent-600 mb-2">HOW TO USE</p>
            <h2 className="text-xl md:text-2xl font-black text-foreground-950 tracking-tight mb-5">使用说明</h2>
            <div className="space-y-3">
              {helpItems.map((item) => (
                <div key={item.step} className="flex gap-3 rounded-lg bg-background-50 border border-background-200 p-4">
                  <span className="text-[13px] font-black text-accent-600 w-6 shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-[14px] font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-1 text-[12px] leading-6 text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-bold tracking-[0.2em] text-accent-600 mb-2">OUR STORY</p>
            <h2 className="text-xl md:text-2xl font-black text-foreground-950 tracking-tight mb-5">站点历程</h2>
            <div className="relative pl-5">
              <span className="absolute left-0 top-2 bottom-2 w-px bg-background-300" />
              {milestones.map((m, i) => (
                <div key={m.year} className={`relative ${i === milestones.length - 1 ? "" : "pb-6"}`}>
                  <span className="absolute -left-5 top-1.5 w-2.5 h-2.5 rounded-full bg-accent-500 border-2 border-background-100" />
                  <p className="text-[12px] font-bold text-accent-700 tracking-wide">{m.year}</p>
                  <h3 className="mt-1 text-[14px] font-semibold text-foreground-950">{m.title}</h3>
                  <p className="mt-1 text-[12px] leading-6 text-foreground-600">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 lg:gap-8">
          <div>
            <p className="text-[11px] font-bold tracking-[0.2em] text-accent-600 mb-2">TEAM</p>
            <h2 className="text-xl md:text-2xl font-black text-foreground-950 tracking-tight mb-5">运营团队</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {team.map((member) => (
                <div key={member.name} className="rounded-lg bg-background-100 border border-background-200 p-4">
                  <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
                    <i className={`${member.icon} text-base`} />
                  </span>
                  <h3 className="mt-3 text-[14px] font-semibold text-foreground-950">{member.name}</h3>
                  <p className="mt-1 text-[12px] leading-5 text-foreground-500">{member.role}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-lg bg-background-100 border border-background-200 p-5">
              <h3 className="text-[14px] font-bold text-foreground-950">内容声明</h3>
              <ul className="mt-3 space-y-2 text-[12px] leading-6 text-foreground-600">
                <li className="flex gap-2"><i className="ri-checkbox-circle-line text-secondary-600 mt-0.5" />站内所有动漫作品、章节与资讯内容均为前端演示数据，不涉及真实版权资源。</li>
                <li className="flex gap-2"><i className="ri-checkbox-circle-line text-secondary-600 mt-0.5" />播放入口仅展示提示，不提供、不引导任何真实视频播放。</li>
                <li className="flex gap-2"><i className="ri-checkbox-circle-line text-secondary-600 mt-0.5" />收藏记录保存在浏览器本地存储，不会上传服务器；清除浏览器数据后会丢失。</li>
              </ul>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg bg-primary-950 p-5">
              <h2 className="text-[14px] font-bold text-background-50">合作与联系</h2>
              <p className="mt-2 text-[12px] leading-6 text-primary-200">
                内容合作、作品收录与信息纠错，欢迎通过以下方式联系运营团队。
              </p>
              <div className="mt-4 space-y-2.5 text-[12px] text-primary-100">
                <p className="flex items-center gap-2"><i className="ri-mail-line text-accent-400" /> partner@yaojing-anime.demo</p>
                <p className="flex items-center gap-2"><i className="ri-time-line text-accent-400" /> 周一至周五 10:00 – 18:00</p>
                <p className="flex items-center gap-2"><i className="ri-map-pin-line text-accent-400" /> 线上内容团队 · 远程协作</p>
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="text-[14px] font-bold text-foreground-950 mb-3">快速入口</h2>
              <div className="grid grid-cols-2 gap-2">
                {categoryMeta.slice(0, 6).map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-md bg-background-50 border border-background-200 text-[12px] text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <i className={`${cat.icon} text-primary-600`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}